<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { bytesToMB, formatClock, formatSpeed } from '@/utils/format'
import { useTaskSpeedPoints } from '@/composables/useTaskSpeedHistory'

/**
 * Per-task speed chart embedded in TaskDetailPanel.
 *
 * Mirrors the dual-axis download/upload SVG used by SpeedChart.vue, but is
 * sized for the side-panel (120px tall by default) and omits the legend /
 * axis captions — the surrounding detail panel already names the task and the
 * download/upload lines. Tooltip still shows time + both speeds.
 */
interface Props {
  gid: string | null
  width?: number | string
  height?: number
}

const props = withDefaults(defineProps<Props>(), {
  width: '100%',
  height: 120,
})

const { t } = useI18n()

const VB_W = 720
const VB_H = 120
// tighter padding than the dashboard chart — we're inside a 420px drawer
const PAD = { top: 8, right: 36, bottom: 18, left: 36 }
const plotW = VB_W - PAD.left - PAD.right
const plotH = VB_H - PAD.top - PAD.bottom

const hoverIndex = ref<number | null>(null)

const points = useTaskSpeedPoints(() => props.gid)

function niceMax(raw: number): number {
  if (!Number.isFinite(raw) || raw <= 0) return 1
  const exp = Math.floor(Math.log10(raw))
  const base = 10 ** exp
  const n = raw / base
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10
  return step * base
}

const maxDownMB = computed(() =>
  niceMax(Math.max(...points.value.map((p) => bytesToMB(p.down)), 0.001)),
)
const maxUpMB = computed(() =>
  niceMax(Math.max(...points.value.map((p) => bytesToMB(p.up)), 0.001)),
)

function xAt(i: number): number {
  const n = points.value.length
  if (n <= 1) return PAD.left
  return PAD.left + (i / (n - 1)) * plotW
}

function yAt(valueMB: number, maxMB: number): number {
  const ratio = maxMB > 0 ? Math.min(valueMB / maxMB, 1) : 0
  return PAD.top + plotH - ratio * plotH
}

function buildLine(key: 'down' | 'up', maxMB: number): string {
  const pts = points.value
  if (pts.length === 0) return ''
  return pts
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${xAt(i).toFixed(2)},${yAt(bytesToMB(p[key]), maxMB).toFixed(2)}`)
    .join(' ')
}

function buildArea(key: 'down' | 'up', maxMB: number): string {
  const line = buildLine(key, maxMB)
  if (!line) return ''
  const lastX = xAt(points.value.length - 1)
  const baseY = PAD.top + plotH
  return `${line} L${lastX.toFixed(2)},${baseY} L${PAD.left},${baseY} Z`
}

const downLine = computed(() => buildLine('down', maxDownMB.value))
const downArea = computed(() => buildArea('down', maxDownMB.value))
const upLine = computed(() => buildLine('up', maxUpMB.value))
const upArea = computed(() => buildArea('up', maxUpMB.value))

const GRID_ROWS = 3
const gridLines = computed(() =>
  Array.from({ length: GRID_ROWS + 1 }, (_, i) => {
    const y = PAD.top + (i / GRID_ROWS) * plotH
    const frac = 1 - i / GRID_ROWS
    return {
      y,
      leftLabel: (maxDownMB.value * frac).toFixed(maxDownMB.value < 10 ? 1 : 0),
      rightLabel: (maxUpMB.value * frac).toFixed(maxUpMB.value < 10 ? 1 : 0),
    }
  }),
)

const xTicks = computed(() => {
  const pts = points.value
  if (pts.length < 2) return []
  const idxs = [0, Math.floor((pts.length - 1) / 2), pts.length - 1]
  return idxs.map((i) => ({ x: xAt(i), label: formatClock(pts[i].ts) }))
})

const hovered = computed(() => {
  const i = hoverIndex.value
  if (i === null || !points.value[i]) return null
  const p = points.value[i]
  return {
    x: xAt(i),
    downY: yAt(bytesToMB(p.down), maxDownMB.value),
    upY: yAt(bytesToMB(p.up), maxUpMB.value),
    time: formatClock(p.ts),
    down: formatSpeed(p.down),
    up: formatSpeed(p.up),
    flip: xAt(i) > PAD.left + plotW * 0.62,
  }
})

function onMove(ev: MouseEvent): void {
  const target = ev.currentTarget as SVGSVGElement | null
  if (!target || points.value.length === 0) return
  const rect = target.getBoundingClientRect()
  if (rect.width === 0) return
  const vbX = ((ev.clientX - rect.left) / rect.width) * VB_W
  const ratio = (vbX - PAD.left) / plotW
  const i = Math.round(ratio * (points.value.length - 1))
  hoverIndex.value = Math.max(0, Math.min(points.value.length - 1, i))
}

function onLeave(): void {
  hoverIndex.value = null
}

const hasData = computed(() =>
  points.value.some((p) => p.down > 0 || p.up > 0),
)
</script>

<template>
  <div class="relative w-full" :style="{ width: typeof width === 'number' ? `${width}px` : width, height: `${height}px` }">
    <div
      v-if="!hasData"
      class="absolute inset-0 flex items-center justify-center text-xs text-fg-subtle"
    >
      {{ t('tasks.detail.speedChart.waiting') }}
    </div>

    <svg
      class="h-full w-full touch-none"
      :viewBox="`0 0 ${VB_W} ${VB_H}`"
      preserveAspectRatio="none"
      role="img"
      :aria-label="t('tasks.detail.speedChart.title')"
      @mousemove="onMove"
      @mouseleave="onLeave"
    >
      <defs>
        <linearGradient id="tsc-down-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="hsl(var(--brand-500))" stop-opacity="0.32" />
          <stop offset="100%" stop-color="hsl(var(--brand-500))" stop-opacity="0.02" />
        </linearGradient>
        <linearGradient id="tsc-up-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="hsl(var(--success))" stop-opacity="0.26" />
          <stop offset="100%" stop-color="hsl(var(--success))" stop-opacity="0.02" />
        </linearGradient>
      </defs>

      <!-- grid -->
      <g>
        <line
          v-for="(g, i) in gridLines"
          :key="`g${i}`"
          :x1="PAD.left"
          :x2="PAD.left + plotW"
          :y1="g.y"
          :y2="g.y"
          stroke="hsl(var(--border))"
          stroke-width="1"
          :stroke-dasharray="i === gridLines.length - 1 ? '0' : '3 3'"
          vector-effect="non-scaling-stroke"
        />
        <text
          v-for="(g, i) in gridLines"
          :key="`l${i}`"
          :x="PAD.left - 6"
          :y="g.y + 3"
          text-anchor="end"
          font-size="10"
          fill="hsl(var(--fg-subtle))"
        >
          {{ g.leftLabel }}
        </text>
        <text
          v-for="(g, i) in gridLines"
          :key="`r${i}`"
          :x="PAD.left + plotW + 6"
          :y="g.y + 3"
          text-anchor="start"
          font-size="10"
          fill="hsl(var(--fg-subtle))"
        >
          {{ g.rightLabel }}
        </text>
      </g>

      <!-- x ticks -->
      <text
        v-for="(t2, i) in xTicks"
        :key="`x${i}`"
        :x="t2.x"
        :y="PAD.top + plotH + 12"
        :text-anchor="i === 0 ? 'start' : i === xTicks.length - 1 ? 'end' : 'middle'"
        font-size="10"
        fill="hsl(var(--fg-subtle))"
      >
        {{ t2.label }}
      </text>

      <!-- upload behind, download in front -->
      <path v-if="upArea" :d="upArea" fill="url(#tsc-up-grad)" />
      <path
        v-if="upLine"
        :d="upLine"
        fill="none"
        stroke="hsl(var(--success))"
        stroke-width="1.5"
        stroke-linejoin="round"
        stroke-linecap="round"
        vector-effect="non-scaling-stroke"
      />
      <path v-if="downArea" :d="downArea" fill="url(#tsc-down-grad)" />
      <path
        v-if="downLine"
        :d="downLine"
        fill="none"
        stroke="hsl(var(--brand-500))"
        stroke-width="1.75"
        stroke-linejoin="round"
        stroke-linecap="round"
        vector-effect="non-scaling-stroke"
      />

      <!-- crosshair -->
      <g v-if="hovered">
        <line
          :x1="hovered.x"
          :x2="hovered.x"
          :y1="PAD.top"
          :y2="PAD.top + plotH"
          stroke="hsl(var(--fg-subtle))"
          stroke-width="1"
          stroke-dasharray="3 3"
          vector-effect="non-scaling-stroke"
        />
        <circle :cx="hovered.x" :cy="hovered.downY" r="2.5" fill="hsl(var(--brand-500))" />
        <circle :cx="hovered.x" :cy="hovered.upY" r="2.5" fill="hsl(var(--success))" />
      </g>
    </svg>

    <!-- tooltip -->
    <div
      v-if="hovered"
      class="pointer-events-none absolute top-1.5 z-10 rounded-md border border-border bg-bg-elevated px-2 py-1 text-xs shadow-md"
      :style="hovered.flip ? { left: '0.5rem' } : { right: '0.5rem' }"
    >
      <div class="mb-0.5 font-medium text-fg-muted">{{ hovered.time }}</div>
      <div class="flex items-center gap-1 text-fg">
        <span class="h-1.5 w-1.5 rounded-full bg-brand-500" />
        <span>↓</span>
        <span class="ml-auto pl-2 font-medium tabular-nums">{{ hovered.down }}</span>
      </div>
      <div class="flex items-center gap-1 text-fg">
        <span class="h-1.5 w-1.5 rounded-full bg-success" />
        <span>↑</span>
        <span class="ml-auto pl-2 font-medium tabular-nums">{{ hovered.up }}</span>
      </div>
    </div>
  </div>
</template>
